import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils";

const badgeVariants = cva(
  "qodo qodo-badge group/badge inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 font-normal leading-normal gap-1 aria-disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        emphasis:
          "border-border-tag-emphasis-idle bg-fill-tag-emphasis-idle text-text-tag-emphasis-idle " +
          "hover:text-text-tag-emphasis-hover hover:bg-fill-tag-emphasis-hover hover:border-border-tag-emphasis-hover " +
          "data-[active=true]:text-text-tag-emphasis-active data-[active=true]:bg-fill-tag-emphasis-active data-[active=true]:border-border-tag-emphasis-active " +
          "aria-disabled:text-text-tag-emphasis-disabled aria-disabled:bg-fill-tag-emphasis-disabled aria-disabled:border-border-tag-emphasis-disabled ",
        neutral:
          "border-border-tag-neutral-idle bg-fill-tag-neutral-idle text-text-tag-neutral-idle " +
          "hover:text-text-tag-neutral-hover hover:bg-fill-tag-neutral-hover hover:border-border-tag-neutral-hover " +
          "data-[active=true]:text-text-tag-neutral-active data-[active=true]:bg-fill-tag-neutral-active data-[active=true]:border-border-tag-neutral-active " +
          "aria-disabled:text-text-tag-neutral-disabled aria-disabled:bg-fill-tag-neutral-disabled aria-disabled:border-border-tag-neutral-disabled ",
        danger:
          "border-border-tag-negative-idle bg-fill-tag-negative-idle text-text-tag-negative-idle " +
          "hover:text-text-tag-negative-hover hover:bg-fill-tag-negative-hover hover:border-border-tag-negative-hover " +
          "data-[active=true]:text-text-tag-negative-active data-[active=true]:bg-fill-tag-negative-active data-[active=true]:border-border-tag-negative-active " +
          "aria-disabled:text-text-tag-negative-disabled aria-disabled:bg-fill-tag-negative-disabled aria-disabled:border-border-tag-negative-disabled ",
        success:
          "border-border-tag-positive-idle bg-fill-tag-positive-idle text-text-tag-positive-idle " +
          "hover:text-text-tag-positive-hover hover:bg-fill-tag-positive-hover hover:border-border-tag-positive-hover " +
          "data-[active=true]:text-text-tag-positive-active data-[active=true]:bg-fill-tag-positive-active data-[active=true]:border-border-tag-positive-active " +
          "aria-disabled:text-text-tag-positive-disabled aria-disabled:bg-fill-tag-positive-disabled aria-disabled:border-border-tag-positive-disabled ",
        warning:
          "border-border-tag-warning-idle bg-fill-tag-warning-idle text-text-tag-warning-idle " +
          "hover:text-text-tag-warning-hover hover:bg-fill-tag-warning-hover hover:border-border-tag-warning-hover " +
          "data-[active=true]:text-text-tag-warning-active data-[active=true]:bg-fill-tag-warning-active data-[active=true]:border-border-tag-warning-active " +
          "aria-disabled:text-text-tag-warning-disabled aria-disabled:bg-fill-tag-warning-disabled aria-disabled:border-border-tag-warning-disabled ",
        inProgress:
          "border-border-tag-in-progress-idle bg-fill-tag-in-progress-idle text-text-tag-in-progress-idle " +
          "hover:text-text-tag-in-progress-hover hover:bg-fill-tag-in-progress-hover hover:border-border-tag-in-progress-hover " +
          "data-[active=true]:text-text-tag-in-progress-active data-[active=true]:bg-fill-tag-in-progress-active data-[active=true]:border-border-tag-in-progress-active " +
          "aria-disabled:text-text-tag-in-progress-disabled aria-disabled:bg-fill-tag-in-progress-disabled aria-disabled:border-border-tag-in-progress-disabled ",
        // Bare variant — no border, bg, or padding. Used by container components (e.g. Select trigger)
        // that need to render content inside a Badge wrapper but want it to look like plain text,
        // letting the parent control the visual styling.
        text: "border-transparent bg-transparent p-0!",
      },
      size: {
        sm: "text-2xs tracking-[0.2px] px-1.5 py-0.5 rounded-md h-4.5",
        lg: "text-xs tracking-[0.12px] px-3 py-0.5 rounded-2xl",
      },
    },
    compoundVariants: [
      {
        size: "sm",
        class:
          "bg-transparent! hover:bg-transparent! data-[active=true]:bg-transparent! aria-disabled:bg-transparent!",
      },
      {
        variant: "text",
        size: "lg",
        class: "text-sm",
      },
      {
        variant: "text",
        size: "sm",
        class: "text-xs",
      },
    ],
    defaultVariants: {
      variant: "emphasis",
      size: "sm",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  disabled?: boolean;
  active?: boolean;
  isClickable?: boolean;
}

function Badge({
  className,
  variant,
  size,
  disabled = false,
  active = false,
  isClickable = false,
  ...props
}: BadgeProps) {
  return (
    <div
      aria-disabled={disabled}
      data-active={isClickable ? active : undefined}
      className={cn(
        badgeVariants({ variant, size }),
        isClickable
          ? "cursor-pointer"
          : // A badge wrapped in a Tooltip must receive pointer events so its
            // hover styles fire alongside the tooltip; other non-clickable
            // badges stay inert.
            "pointer-events-none cursor-default [[data-slot=tooltip-trigger]_&]:pointer-events-auto",
        className,
      )}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
